import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import OutsideClickHandler from '../OutsideClickHandler/OutsideClickHandler';
import IconClock from '../IconClock/IconClock';
import css from './TimePicker.module.css';

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5); // 0, 5, 10, ... 55
const AMPMS = ['AM', 'PM'];

const formatTime = (hour, minute, ampm) => {
    let h = parseInt(hour, 10);
    const m = parseInt(minute, 10);

    if (ampm === 'PM' && h < 12) h += 12;
    if (ampm === 'AM' && h === 12) h = 0;

    const hh = h.toString().padStart(2, '0');
    const mm = m.toString().padStart(2, '0');
    return `${hh}:${mm}`;
};

const parseTime = (value) => {
    if (!value) return null;
    const [hStr, mStr] = value.split(':');
    let h = parseInt(hStr, 10);
    const m = parseInt(mStr, 10);

    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12;
    h = h === 0 ? 12 : h;

    return { hour: h, minute: m, ampm };
};

const TimePicker = props => {
    const {
        id,
        rootClassName,
        className,
        inputClassName,
        value,
        onChange,
        onBlur,
        onFocus,
        placeholder,
        disabled,
        readOnly,
        ...rest
    } = props;

    const [isOpen, setIsOpen] = useState(false);
    const [internalValue, setInternalValue] = useState(parseTime(value) || { hour: 12, minute: 0, ampm: 'AM' });

    useEffect(() => {
        if (value) {
            setInternalValue(parseTime(value));
        }
    }, [value]);

    const handleToggle = () => {
        if (!disabled && !readOnly) {
            setIsOpen(!isOpen);
            if (!isOpen && onFocus) onFocus();
        }
    };

    const handleClose = () => {
        setIsOpen(false);
        if (onBlur) onBlur();
    };

    const handleSelect = (type, val) => {
        const newValue = { ...internalValue, [type]: val };
        setInternalValue(newValue);

        const timeString = formatTime(newValue.hour, newValue.minute, newValue.ampm);
        onChange(timeString);
    };

    // Scroll into view logic could be added here, but simplest is just rendering the lists

    const classes = classNames(rootClassName || css.root, className);
    const inputWrapperClasses = classNames(css.inputWrapper, {
        [css.isOpen]: isOpen,
        [inputClassName]: inputClassName,
    });

    const displayValue = value ?
        `${internalValue.hour}:${internalValue.minute.toString().padStart(2, '0')} ${internalValue.ampm}` :
        '';

    return (
        <OutsideClickHandler onOutsideClick={handleClose} className={classes}>
            <div
                className={inputWrapperClasses}
                onClick={handleToggle}
                id={`${id}_wrapper`}
            >
                <IconClock className={css.icon} />
                <input
                    id={id}
                    className={css.input}
                    value={displayValue}
                    placeholder={placeholder || "Select time"}
                    readOnly
                    disabled={disabled}
                    {...rest}
                />
            </div>

            {isOpen && (
                <div className={css.popup}>
                    <div className={css.pickerContent}>
                        <div className={css.column}>
                            <div className={css.columnHeader}>Hr</div>
                            {HOURS.map(h => (
                                <div
                                    key={h}
                                    className={classNames(css.columnItem, { [css.selected]: internalValue.hour === h })}
                                    onClick={() => handleSelect('hour', h)}
                                >
                                    {h}
                                </div>
                            ))}
                        </div>
                        <div className={css.column}>
                            <div className={css.columnHeader}>Min</div>
                            {MINUTES.map(m => (
                                <div
                                    key={m}
                                    className={classNames(css.columnItem, { [css.selected]: internalValue.minute === m })}
                                    onClick={() => handleSelect('minute', m)}
                                >
                                    {m.toString().padStart(2, '0')}
                                </div>
                            ))}
                        </div>
                        <div className={css.column}>
                            <div className={css.columnHeader}>Pre</div>
                            {AMPMS.map(a => (
                                <div
                                    key={a}
                                    className={classNames(css.columnItem, { [css.selected]: internalValue.ampm === a })}
                                    onClick={() => handleSelect('ampm', a)}
                                >
                                    {a}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </OutsideClickHandler>
    );
};

TimePicker.defaultProps = {
    rootClassName: null,
    className: null,
    inputClassName: null,
    value: null,
    onChange: () => { },
    placeholder: null,
    disabled: false,
    readOnly: false,
};

TimePicker.propTypes = {
    id: PropTypes.string.isRequired,
    rootClassName: PropTypes.string,
    className: PropTypes.string,
    inputClassName: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    readOnly: PropTypes.bool,
};

export default TimePicker;
